import { format } from 'date-fns'

import { api_gateway } from '../../conexoes/api'
import { decrypt, formatBusca } from '../../utils/genericFormatters'
import { requestResolver } from '../../utils/requestResolver'

const getParams = (busca, separadorLinha) => {
  if (!busca) return ''
  return formatBusca(busca, separadorLinha)
}

function transformData(data) {
  const address = {
    ...(data.CIDADE?.length && {
      CIDADE: data.CIDADE.map((city) => ({
        cityId: city.value,
        stateId: city.stateId
      }))
    }),
    ...(data.ESTADO && { ESTADO: data.ESTADO })
  }

  const result = {
    ...data,
    ADDRESS: Object.keys(address).length ? address : null
  }

  if (result.CIDADE) delete result.CIDADE
  if (result.ESTADO) delete result.ESTADO

  const finalFiltros = Object.fromEntries(
    Object.entries(result).filter(
      ([, v]) => v && (!Array.isArray(v) || v.length > 0)
    )
  )

  return finalFiltros
}

const buildRequestBody = (body) => {
  const auxFilter =
    body.filter?.vazio || Object.keys(body.filter || {}).length === 0
      ? undefined
      : transformData(body.filter)

  return {
    ...body,
    filter: auxFilter
  }
}

export const PortadorasService = {
  //#region GET
  getPortadoras: (
    pagina,
    linhasPorPagina,
    filtros = {},
    busca,
    separadorLinhaBusca,
    count
  ) => {
    const auxFilter =
      filtros.vazio || Object.keys(filtros).length === 0
        ? undefined
        : transformData(filtros)

    const params = {
      params: {
        page: pagina,
        pageRows: linhasPorPagina,
        count,
        filter: auxFilter,
        fetch: busca ? getParams(busca, separadorLinhaBusca) : undefined
      }
    }

    Object.keys(params.params).forEach(
      (key) => params.params[key] === undefined && delete params.params[key]
    )

    return requestResolver(api_gateway.get(`/api/v2/carriers/fetchAll`, params))
  },
  getCarrierFilter: () => {
    return requestResolver(api_gateway.get(`/api/v2/carriers/filterCarriers`))
  },
  getTechnologiesFilter: () => {
    return requestResolver(
      api_gateway.get(`/api/v2/carriers/filterTechnologies`)
    )
  },
  getCarriersTypesFilter: () => {
    return requestResolver(api_gateway.get('/api/v2/carriers/types'))
  },
  getRadioCustomers: (
    pagina,
    linhasPorPagina,
    filtros = {},
    count,
    cdPortadora
  ) => {
    const params = {
      params: {
        page: pagina,
        pageRows: linhasPorPagina,
        count,
        filter: filtros.vazio ? undefined : filtros,
        id: cdPortadora
      }
    }

    Object.keys(params.params).forEach(
      (key) => params.params[key] === undefined && delete params.params[key]
    )

    return requestResolver(
      api_gateway.get(`/api/v2/carriers/details/radioCustomers`, params)
    )
  },
  getSimcards: (pagina, linhasPorPagina, count, cdPortadora) => {
    const params = {
      params: {
        page: pagina,
        pageRows: linhasPorPagina,
        count
      }
    }

    Object.keys(params.params).forEach(
      (key) => params.params[key] === undefined && delete params.params[key]
    )

    return requestResolver(
      api_gateway.get(`/api/v2/carriers/${cdPortadora}/devices`, params)
    )
  },
  getFatContas: () => {
    return requestResolver(
      api_gateway.get(
        `/m1/consultar/fat_conta/contas_usuario?cd_pav_usuario=${
          decrypt(['codigo'])?.codigo
        }`
      )
    )
  },
  getAlterarToken: async ({ codigo, codigoErp }) => {
    const fatContas = await PortadorasService.getFatContas().then(
      (response) => {
        return response?.map((item) => item.cd_fat_conta).join(',')
      }
    )

    const decryptedParams = decrypt([
      'login',
      'email',
      'codigo',
      'nomeDeUsuario',
      'perfil',
      'cdSmsContaUsuario',
      'id',
      'tipoPerfil',
      'clientePrimario',
      'clienteOriginal',
      'clienteNivel'
    ])

    const params = {
      ...decryptedParams,
      fatContas,
      codigoCliente: codigo,
      codigoClienteTexto: codigoErp,
      codigoClienteErpAntigo: codigoErp
    }

    return api_gateway.get('/m1/servicos/alterar_token', { params })
  },
  getAtualizarLogin: () => {
    const params = {
      cd_pav_usuario: decrypt(['codigo'])
    }

    return api_gateway.get('/m1/servicos/token/atualizarLogin', { params })
  },
  getAddressFilter: (ids) => {
    return requestResolver(
      api_gateway.post(`/api/v2/carriers/filterAddress`, {
        stateID: ids
      })
    )
  },
  getLocation: (cdPortadora) => {
    return requestResolver(
      api_gateway.get(`/api/v2/carriers/${cdPortadora}/location`)
    )
  },
  getMapInformation: (filtros) => {
    const params = {
      params: {
        cd_pav_cliente: decrypt(['codigoCliente']),
        filter: filtros.vazio ? undefined : filtros
      }
    }

    return requestResolver(
      api_gateway.get('/api/v2/carriers/retrieveMapInformation', params)
    )
  },
  getDevicesPortadora: (filtros = {}, busca, separadorLinhaBusca) => {
    const hasSelectedSimcards = filtros.CD_SIMCARD

    const params = {
      params: {
        filtro:
          filtros.vazio || Object.keys(filtros).length === 0
            ? undefined
            : filtros,
        busca: hasSelectedSimcards
          ? undefined
          : busca
            ? getParams(busca, separadorLinhaBusca)
            : undefined
      }
    }

    return requestResolver(api_gateway.get('/api/v2/devices/portadora', params))
  },

  getHistorico: (id_portadora, tipo, periodo) => {
    const horasPorTipo = {
      '24h': 24 * 1,
      '48h': 24 * 2,
      '7d': 24 * 7,
      '15d': 24 * 15,
      '30d': 24 * 30
    }

    let params = ''

    if (tipo === 'periodo' && periodo.start && periodo.end) {
      const formattedStart = format(new Date(periodo.start), 'yyyy-MM-dd')
      const formattedEnd = format(new Date(periodo.end), 'yyyy-MM-dd')

      params = `start=${formattedStart}&stop=${formattedEnd}`
    } else {
      if (tipo === 'periodo') return

      params = `hours=${horasPorTipo[tipo]}`
    }

    return requestResolver(
      api_gateway.get(`/api/v2/carriers/${id_portadora}/history?${params}`)
    )
  },

  getConsumo: (id_portadora, tipo) => {
    const horasPorTipo = {
      '24h': 24 * 1,
      '2d': 24 * 2,
      '3d': 24 * 3,
      '4d': 24 * 4,
      '5d': 24 * 5,
      '6d': 24 * 6,
      '7d': 24 * 7
    }

    let params = `hours=${horasPorTipo[tipo]}`

    return requestResolver(
      api_gateway.get(`/api/v2/carriers/${id_portadora}/consumption?${params}`)
    )
  },

  //#endregion GET

  //#region CREATE
  createReport: (body) => {
    return requestResolver(
      api_gateway.post(`/api/v2/carriers/exportData`, body)
    )
  },
  createReportSimcards: ({
    filter = {},
    fetch,
    separadorLinhaBusca,
    reportType,
    carrierID
  }) => {
    const auxFilter =
      filter.vazio || Object.keys(filter).length === 0
        ? undefined
        : transformData(filter)

    const params = {
      reportType,
      carrierID,
      filter: auxFilter,
      fetch: fetch ? getParams(fetch, separadorLinhaBusca) : undefined
    }

    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key]
    )

    return requestResolver(
      api_gateway.post(`/api/v2/carriers/exportData/devices`, params)
    )
  },
  createHistoryReport: (body) => {
    const auxBody = buildRequestBody(body)

    return requestResolver(
      api_gateway.post(`/api/v2/carriers/report/history`, auxBody)
    )
  },

  createConsumptionReport: (body) => {
    const auxBody = buildRequestBody(body)

    return requestResolver(
      api_gateway.post(`/api/v2/carriers/report/consumption`, auxBody)
    )
  },

  //#endregion CREATE

  //#region UPDATE
  updateCarrierName: (cdPortadora, nome) => {
    const params = {
      cd_pav_cliente: decrypt(['codigoCliente']),
      carrierID: cdPortadora,
      carrierName: nome
    }

    return api_gateway.post('/api/v2/carriers/updateCarrierName', params)
  }
  //#endregion UPDATE
}
